<script lang="ts">
  import { onMount } from 'svelte';
  import { firmListStore } from '@entities/firm/store.svelte';
  import FirmTable from '@widgets/firm-table/FirmTable.svelte';
  import FirmBulkAssigneeModal from '@features/firm-bulk-assignee/FirmBulkAssigneeModal.svelte';
  import FirmAdvancedFilterDrawer from '@features/firm-advanced-filter/FirmAdvancedFilterDrawer.svelte';
  import KebabMenu from '@shared/ui/KebabMenu.svelte';
  import Pagination from '@shared/ui/Pagination.svelte';
  import { listProducts } from '@shared/api/products';
  import { competitorServicesApi } from '@shared/api/competitor-services';
  import { listAssignableAccounts } from '@shared/api/accounts';
  import { parsePaged } from '@shared/api/paging';
  import { parseProduct } from '@domain/product/parseProduct';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import {
    CLIENT_RATIO_CATEGORY_OPTIONS,
    CONTRACT_FILTER_OPTIONS,
    CUSTOMER_STATUS_LABELS,
    LAST_CONTACT_BUCKET_OPTIONS,
    LAWYER_COUNT_BUCKET_OPTIONS,
    LEVEL_LMH_OPTIONS,
    NEXT_ACTION_BUCKET_OPTIONS,
    ORGANIZATION_TYPE_OPTIONS,
    FIRM_PRACTICE_AREA_OPTIONS,
    formatBarAssociationLabel,
    isDefaultCustomerStatuses,
    type SimpleOption,
  } from '@shared/constants/choices';
  import {
    ACTION_TYPE_LABELS,
    type ActionType,
  } from '@shared/types/sales-action';
  import type {
    FirmBulkAssigneeResult,
    FirmListQuery,
  } from '@shared/types/firm';
  import { canEdit } from '@shared/types/session';
  import { MAX_LIST_VIEW_SEARCH_LENGTH } from '@shared/lib/list-view-state';
  import {
    createListViewPersistence,
    type ListViewPersistence,
  } from '@features/list-view-state/persistence';
  import {
    withUnknownSelectedOptions,
    formatUnknownOptionLabel,
  } from '@features/list-view-state/unknown-options';
  import {
    createDefaultFirmListQuery,
    serializeFirmListQuery,
    validateFirmListQuery,
  } from './firm-list-view-state';

  type FilterSummaryChip = {
    key: string;
    label: string;
    onRemove: () => void;
  };

  type BulkSelectionMode = 'ids' | 'query';
  const QUERY_ONLY_BULK_MODES: BulkSelectionMode[] = ['query'];
  const IDS_ONLY_BULK_MODES: BulkSelectionMode[] = ['ids'];

  let query = $state<FirmListQuery>(createDefaultFirmListQuery());

  // 初期 query に q は無いので空文字で開始する。 以降は検索ボックス自身が更新する。
  let qInput = $state('');
  // 契約中の自社ソリューションフィルタの選択肢。列ヘッダーの FilterChip に渡す。
  let productOptions = $state<SimpleOption[]>([]);
  // 詳細画面の「利用ソリューション」 (`customer_competitor_services`) 用。
  let competitorServiceOptions = $state<SimpleOption[]>([]);
  // 担当者フィルタ/一括割り当て候補。API 側の assignable accounts から invited / active のみ使う。
  let accountOptions = $state<SimpleOption[]>([]);
  // 既存割当の表示名解決用。inactive な担当者も UUID 表示に落とさないため全件を保持する。
  let accountLabelOptions = $state<SimpleOption[]>([]);
  let accountLoadError = $state<string | null>(null);
  let persistence: ListViewPersistence | null = null;
  let selectedIds = $state<Set<string>>(new Set());
  let selectionModeActive = $state(false);
  let bulkModalOpen = $state(false);
  let bulkInitialSelectionMode = $state<BulkSelectionMode>('ids');
  let bulkAllowedSelectionModes = $state<BulkSelectionMode[]>([
    ...QUERY_ONLY_BULK_MODES,
  ]);
  let bulkStatusMessage = $state<string | null>(null);
  let advancedFilterOpen = $state(false);
  const countFormatter = new Intl.NumberFormat('ja-JP');
  const selectedFirmIds = $derived(Array.from(selectedIds));
  const selectedCountLabel = $derived(
    countFormatter.format(selectedFirmIds.length),
  );
  const visibleFirmIds = $derived(
    firmListStore.data?.items.map((firm) => firm.id) ?? [],
  );
  const allVisibleRowsSelected = $derived(
    visibleFirmIds.length > 0 &&
      visibleFirmIds.every((id) => selectedIds.has(id)),
  );
  const allowBulkEdit = $derived(
    (sessionStore.user ? canEdit(sessionStore.user.role) : false) &&
      !accountLoadError,
  );
  const productFilterOptions = $derived(
    withUnknownSelectedOptions(productOptions, query.product_ids),
  );
  const competitorServiceFilterOptions = $derived(
    withUnknownSelectedOptions(
      competitorServiceOptions,
      query.competitor_service_ids,
    ),
  );
  const accountFilterOptions = $derived(
    withUnknownSelectedOptions(
      accountOptions,
      query.assignee_account_ids,
      // 候補 (invited/active) に無い担当者は全件リストで実名解決する。 それでも
      // 解決できなければ「不明な項目 (…)」表示。 FirmTable のセル表示と整合させる。
      (value) =>
        accountLabelOptions.find((o) => o.value === value)?.label ??
        formatUnknownOptionLabel(value),
    ),
  );

  $effect(() => {
    if (!allowBulkEdit) {
      selectedIds = new Set();
      selectionModeActive = false;
      bulkModalOpen = false;
      bulkStatusMessage = null;
    }
  });

  $effect(() => {
    if (firmListStore.data && firmListStore.data.total === 0) {
      selectedIds = new Set();
      selectionModeActive = false;
    }
  });

  const bulkMenuDisabled = $derived(
    firmListStore.data === null ||
      firmListStore.error !== null ||
      firmListStore.loading ||
      (firmListStore.data?.total ?? 0) === 0,
  );
  const bulkSelectionDisabled = $derived(
    firmListStore.loading || firmListStore.error !== null,
  );
  const selectVisibleRowsDisabled = $derived(
    bulkSelectionDisabled ||
      visibleFirmIds.length === 0 ||
      allVisibleRowsSelected,
  );
  const assignSelectedRowsDisabled = $derived(
    bulkSelectionDisabled || selectedFirmIds.length === 0,
  );
  const hasActiveFilters = $derived(
    qInput.trim() !== '' ||
      (query.q?.trim() ?? '') !== '' ||
      (query.cities?.length ?? 0) > 0 ||
      (query.lawyer_count_buckets?.length ?? 0) > 0 ||
      (query.bar_associations?.length ?? 0) > 0 ||
      (query.prefectures?.length ?? 0) > 0 ||
      (query.organization_types?.length ?? 0) > 0 ||
      (query.practice_areas?.length ?? 0) > 0 ||
      (query.client_ratio_categories?.length ?? 0) > 0 ||
      (query.it_literacies?.length ?? 0) > 0 ||
      query.bulk_sales_all_excluded !== undefined ||
      query.is_competitor !== undefined ||
      (query.competitor_service_ids?.length ?? 0) > 0 ||
      query.competitor_service_presence !== undefined ||
      (query.product_ids?.length ?? 0) > 0 ||
      (query.assignee_account_ids?.length ?? 0) > 0 ||
      query.unassigned_only === true ||
      query.phone_presence !== undefined ||
      query.legal_training_term_from !== undefined ||
      query.legal_training_term_to !== undefined ||
      query.contract_status_filter !== undefined ||
      (query.next_action_types?.length ?? 0) > 0 ||
      query.last_contact_bucket !== undefined ||
      query.next_action_bucket !== undefined ||
      !isDefaultCustomerStatuses(query.top_sales_grades),
  );

  const PHONE_PRESENCE_LABELS: Record<string, string> = {
    present: '電話番号あり',
    missing: '電話番号なし',
  };

  const IT_LITERACY_FILTER_OPTIONS: SimpleOption[] = [
    ...LEVEL_LMH_OPTIONS,
    { value: 'unset', label: '未設定' },
  ];

  const COMPETITOR_SERVICE_PRESENCE_LABELS: Record<string, string> = {
    present: '登録あり',
    missing: '登録なし',
  };

  const TERM_SCOPE_LABELS: Record<string, string> = {
    any: '所属弁護士のいずれかが該当',
    representative: '代表弁護士の期',
    oldest: '最も古い期の弁護士',
  };

  const activeFilterChips = $derived(buildActiveFilterChips());

  function labelFromOptions(options: SimpleOption[], value: string): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }

  function formatValues(
    values: string[],
    labeler: (value: string) => string,
  ): string {
    const labels = values.map(labeler);
    if (labels.length <= 1) return labels[0] ?? '';
    return `${labels[0]} 他${labels.length - 1}件`;
  }

  function removeFilter(patch: Partial<FirmListQuery>): void {
    if ('q' in patch) qInput = '';
    applyQuery({ ...query, ...patch, page: 1 });
  }

  function termRangeLabel(): string {
    const from = query.legal_training_term_from;
    const to = query.legal_training_term_to;
    if (from != null && to != null) return `${from}〜${to}期`;
    if (from != null) return `${from}期以上`;
    if (to != null) return `${to}期以下`;
    return '';
  }

  function buildActiveFilterChips(): FilterSummaryChip[] {
    const chips: FilterSummaryChip[] = [];
    const q = query.q?.trim();
    if (q) {
      chips.push({
        key: 'q',
        label: `検索: ${q}`,
        onRemove: () => removeFilter({ q: undefined }),
      });
    }
    if ((query.cities?.length ?? 0) > 0) {
      chips.push({
        key: 'cities',
        label: `市区町村: ${formatValues(query.cities ?? [], (value) => value)}`,
        onRemove: () => removeFilter({ cities: undefined }),
      });
    }
    if ((query.bar_associations?.length ?? 0) > 0) {
      chips.push({
        key: 'bar_associations',
        label: `弁護士会: ${formatValues(
          query.bar_associations ?? [],
          (value) => formatBarAssociationLabel(value),
        )}`,
        onRemove: () => removeFilter({ bar_associations: undefined }),
      });
    }
    if ((query.organization_types?.length ?? 0) > 0) {
      chips.push({
        key: 'organization_types',
        label: `組織形態: ${formatValues(
          query.organization_types ?? [],
          (value) => labelFromOptions(ORGANIZATION_TYPE_OPTIONS, value),
        )}`,
        onRemove: () => removeFilter({ organization_types: undefined }),
      });
    }
    if ((query.practice_areas?.length ?? 0) > 0) {
      chips.push({
        key: 'practice_areas',
        label: `主要取扱分野: ${formatValues(
          query.practice_areas ?? [],
          (value) => labelFromOptions(FIRM_PRACTICE_AREA_OPTIONS, value),
        )}`,
        onRemove: () => removeFilter({ practice_areas: undefined }),
      });
    }
    if ((query.client_ratio_categories?.length ?? 0) > 0) {
      chips.push({
        key: 'client_ratio_categories',
        label: `クライアント比率: ${formatValues(
          query.client_ratio_categories ?? [],
          (value) => labelFromOptions(CLIENT_RATIO_CATEGORY_OPTIONS, value),
        )}`,
        onRemove: () => removeFilter({ client_ratio_categories: undefined }),
      });
    }
    if ((query.it_literacies?.length ?? 0) > 0) {
      chips.push({
        key: 'it_literacies',
        label: `ITリテラシー: ${formatValues(
          query.it_literacies ?? [],
          (value) => labelFromOptions(IT_LITERACY_FILTER_OPTIONS, value),
        )}`,
        onRemove: () => removeFilter({ it_literacies: undefined }),
      });
    }
    if (query.bulk_sales_all_excluded !== undefined) {
      chips.push({
        key: 'bulk_sales_all_excluded',
        label: query.bulk_sales_all_excluded
          ? '一括営業すべて対象外'
          : '一括営業すべて対象外ではない',
        onRemove: () => removeFilter({ bulk_sales_all_excluded: undefined }),
      });
    }
    if (query.is_competitor !== undefined) {
      chips.push({
        key: 'is_competitor',
        label: query.is_competitor ? '競合' : '競合ではない',
        onRemove: () => removeFilter({ is_competitor: undefined }),
      });
    }
    if ((query.competitor_service_ids?.length ?? 0) > 0) {
      chips.push({
        key: 'competitor_service_ids',
        label: `登録済み利用ソリューション: ${formatValues(
          query.competitor_service_ids ?? [],
          (value) => labelFromOptions(competitorServiceFilterOptions, value),
        )}`,
        onRemove: () => removeFilter({ competitor_service_ids: undefined }),
      });
    }
    if (query.competitor_service_presence) {
      chips.push({
        key: 'competitor_service_presence',
        label: `利用ソリューション: ${COMPETITOR_SERVICE_PRESENCE_LABELS[query.competitor_service_presence]}`,
        onRemove: () =>
          removeFilter({ competitor_service_presence: undefined }),
      });
    }
    if ((query.lawyer_count_buckets?.length ?? 0) > 0) {
      chips.push({
        key: 'lawyer_count_buckets',
        label: `弁護士数: ${formatValues(
          query.lawyer_count_buckets ?? [],
          (value) => labelFromOptions(LAWYER_COUNT_BUCKET_OPTIONS, value),
        )}`,
        onRemove: () => removeFilter({ lawyer_count_buckets: undefined }),
      });
    }
    if ((query.prefectures?.length ?? 0) > 0) {
      chips.push({
        key: 'prefectures',
        label: `都道府県: ${formatValues(query.prefectures ?? [], (value) => value)}`,
        onRemove: () => removeFilter({ prefectures: undefined }),
      });
    }
    if ((query.product_ids?.length ?? 0) > 0) {
      chips.push({
        key: 'product_ids',
        label: `契約中の自社ソリューション: ${formatValues(
          query.product_ids ?? [],
          (value) => labelFromOptions(productFilterOptions, value),
        )}`,
        onRemove: () => removeFilter({ product_ids: undefined }),
      });
    }
    if (query.unassigned_only) {
      chips.push({
        key: 'unassigned_only',
        label: '担当者: 未担当',
        onRemove: () => removeFilter({ unassigned_only: undefined }),
      });
    }
    if ((query.assignee_account_ids?.length ?? 0) > 0) {
      chips.push({
        key: 'assignee_account_ids',
        label: `担当者: ${formatValues(
          query.assignee_account_ids ?? [],
          (value) => labelFromOptions(accountFilterOptions, value),
        )}`,
        onRemove: () => removeFilter({ assignee_account_ids: undefined }),
      });
    }
    if (query.phone_presence) {
      chips.push({
        key: 'phone_presence',
        label: `電話番号: ${PHONE_PRESENCE_LABELS[query.phone_presence]}`,
        onRemove: () => removeFilter({ phone_presence: undefined }),
      });
    }
    if (
      query.legal_training_term_from != null ||
      query.legal_training_term_to != null
    ) {
      const scope = query.legal_training_term_scope ?? 'any';
      chips.push({
        key: 'legal_training_term',
        label: `期: ${TERM_SCOPE_LABELS[scope]} ${termRangeLabel()}`,
        onRemove: () =>
          removeFilter({
            legal_training_term_scope: undefined,
            legal_training_term_from: undefined,
            legal_training_term_to: undefined,
          }),
      });
    }
    if (!isDefaultCustomerStatuses(query.top_sales_grades)) {
      chips.push({
        key: 'top_sales_grades',
        label: `ステータス: ${formatValues(
          query.top_sales_grades ?? [],
          (value) => CUSTOMER_STATUS_LABELS[value] ?? value,
        )}`,
        onRemove: () => removeFilter({ top_sales_grades: undefined }),
      });
    }
    if (query.last_contact_bucket) {
      chips.push({
        key: 'last_contact_bucket',
        label: `最終接触: ${labelFromOptions(LAST_CONTACT_BUCKET_OPTIONS, query.last_contact_bucket)}`,
        onRemove: () => removeFilter({ last_contact_bucket: undefined }),
      });
    }
    if (query.next_action_bucket) {
      chips.push({
        key: 'next_action_bucket',
        label: `対応期限: ${labelFromOptions(NEXT_ACTION_BUCKET_OPTIONS, query.next_action_bucket)}`,
        onRemove: () => removeFilter({ next_action_bucket: undefined }),
      });
    }
    if ((query.next_action_types?.length ?? 0) > 0) {
      chips.push({
        key: 'next_action_types',
        label: `次アクション: ${formatValues(
          query.next_action_types ?? [],
          (value) => ACTION_TYPE_LABELS[value as ActionType] ?? value,
        )}`,
        onRemove: () => removeFilter({ next_action_types: undefined }),
      });
    }
    if (query.contract_status_filter) {
      chips.push({
        key: 'contract_status_filter',
        label: `契約: ${labelFromOptions(CONTRACT_FILTER_OPTIONS, query.contract_status_filter)}`,
        onRemove: () => removeFilter({ contract_status_filter: undefined }),
      });
    }
    return chips;
  }

  function openAdvancedFilter(): void {
    advancedFilterOpen = true;
  }

  function closeAdvancedFilter(): void {
    advancedFilterOpen = false;
  }

  async function refresh() {
    selectedIds = new Set();
    await firmListStore.refresh(query);
  }

  function applyQuery(next: FirmListQuery) {
    bulkStatusMessage = null;
    query = next;
    // スクロールを先頭へ戻し、 適用済み条件を保存する (localStorage + session)。
    persistence?.onQueryApplied();
    void refresh();
  }

  function submitSearch(ev: Event): void {
    ev.preventDefault();
    const q = qInput.trim().slice(0, MAX_LIST_VIEW_SEARCH_LENGTH);
    qInput = q;
    applyQuery({ ...query, q: q || undefined, page: 1 });
  }

  function toggleSort(field: NonNullable<FirmListQuery['sort_by']>) {
    let next: FirmListQuery;
    if (query.sort_by === field) {
      next = {
        ...query,
        sort_dir: query.sort_dir === 'asc' ? 'desc' : 'asc',
        page: 1,
      };
    } else {
      next = { ...query, sort_by: field, sort_dir: 'asc', page: 1 };
    }
    applyQuery(next);
  }

  function changePage(page: number) {
    bulkStatusMessage = null;
    query = { ...query, page };
    persistence?.onPageChanged();
    void refresh();
  }

  function clearFilters(): void {
    bulkStatusMessage = null;
    qInput = '';
    applyQuery({
      ...query,
      q: undefined,
      cities: undefined,
      lawyer_count_buckets: undefined,
      bar_associations: undefined,
      prefectures: undefined,
      organization_types: undefined,
      practice_areas: undefined,
      client_ratio_categories: undefined,
      it_literacies: undefined,
      bulk_sales_all_excluded: undefined,
      is_competitor: undefined,
      competitor_service_ids: undefined,
      competitor_service_presence: undefined,
      product_ids: undefined,
      assignee_account_ids: undefined,
      unassigned_only: undefined,
      phone_presence: undefined,
      legal_training_term_scope: undefined,
      legal_training_term_from: undefined,
      legal_training_term_to: undefined,
      contract_status_filter: undefined,
      top_sales_grades: undefined,
      last_contact_bucket: undefined,
      next_action_bucket: undefined,
      next_action_types: undefined,
      page: 1,
    });
  }

  async function loadProducts() {
    try {
      const products = parsePaged(
        await listProducts({ page_size: 200 }),
        parseProduct,
      );
      productOptions = products.items.map((p) => ({
        value: p.id,
        label: p.name,
      }));
    } catch {
      productOptions = [];
    }
  }

  async function loadCompetitorServices() {
    try {
      const response = await competitorServicesApi.list();
      competitorServiceOptions = response.items.map((service) => ({
        value: service.id,
        label: service.name,
      }));
    } catch {
      competitorServiceOptions = [];
    }
  }

  async function loadAccounts() {
    try {
      accountLoadError = null;
      const accounts = await listAssignableAccounts();
      accountLabelOptions = accounts.map((a) => ({
        value: a.id,
        label: a.display_name,
      }));
      accountOptions = accounts
        .filter((a) => a.status === 'invited' || a.status === 'active')
        .map((a) => ({
          value: a.id,
          label: a.display_name,
        }));
    } catch (err) {
      accountOptions = [];
      accountLabelOptions = [];
      accountLoadError = err instanceof Error ? err.message : String(err);
    }
  }

  function toggleRow(id: string, checked: boolean): void {
    if (!selectionModeActive || bulkSelectionDisabled) return;
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    selectedIds = next;
    bulkStatusMessage = null;
  }

  function toggleAll(checked: boolean): void {
    if (!selectionModeActive || bulkSelectionDisabled) return;
    const next = new Set(selectedIds);
    const ids = firmListStore.data?.items.map((firm) => firm.id) ?? [];
    for (const id of ids) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    selectedIds = next;
    bulkStatusMessage = null;
  }

  function selectVisibleRows(): void {
    if (selectVisibleRowsDisabled) return;
    toggleAll(true);
  }

  function openBulkAssignee(
    mode: BulkSelectionMode,
    allowedSelectionModes: BulkSelectionMode[],
  ): void {
    bulkInitialSelectionMode = mode;
    bulkAllowedSelectionModes = [...allowedSelectionModes];
    bulkModalOpen = true;
  }

  function openBulkAssigneeForQuery(): void {
    if (!allowBulkEdit || bulkMenuDisabled) return;
    selectedIds = new Set();
    selectionModeActive = false;
    bulkStatusMessage = null;
    openBulkAssignee('query', QUERY_ONLY_BULK_MODES);
  }

  function startRowSelectionMode(): void {
    if (!allowBulkEdit || bulkMenuDisabled) return;
    selectedIds = new Set();
    selectionModeActive = true;
    bulkStatusMessage = null;
  }

  function cancelSelectionMode(): void {
    selectedIds = new Set();
    selectionModeActive = false;
    bulkStatusMessage = null;
  }

  function openBulkAssigneeForSelectedIds(): void {
    if (!allowBulkEdit || assignSelectedRowsDisabled) return;
    bulkStatusMessage = null;
    openBulkAssignee('ids', IDS_ONLY_BULK_MODES);
  }

  function closeBulkAssignee(): void {
    bulkModalOpen = false;
    selectedIds = new Set();
    selectionModeActive = false;
    bulkStatusMessage = null;
  }

  function formatBulkResult(result: FirmBulkAssigneeResult): string {
    return `一括担当割り当てを実行しました。対象 ${result.matched_count} 件 / 更新 ${result.update_count} 件 / 変更なし ${result.unchanged_count} 件 / 更新されない ${result.skipped_count} 件`;
  }

  function handleBulkDone(result: FirmBulkAssigneeResult): void {
    bulkModalOpen = false;
    selectedIds = new Set();
    selectionModeActive = false;
    bulkStatusMessage = formatBulkResult(result);
    void refresh();
  }

  onMount(() => {
    void loadProducts();
    void loadCompetitorServices();
    void loadAccounts();

    const user = sessionStore.user;
    const p = createListViewPersistence<FirmListQuery>({
      screen: 'firms',
      identity: user ? { userId: user.id, orgId: user.orgId } : null,
      createDefaults: createDefaultFirmListQuery,
      validate: validateFirmListQuery,
      serialize: serializeFirmListQuery,
      getQuery: () => query,
      setQuery: (next) => {
        query = next;
      },
      syncSearchInput: (next) => {
        qInput = next.q ?? '';
      },
      refresh,
      getListState: () =>
        firmListStore.data
          ? {
              total: firmListStore.data.total,
              page_size: firmListStore.data.page_size,
            }
          : null,
      getListError: () => firmListStore.error,
    });
    persistence = p;
    void p.initialize();

    return () => {
      persistence = null;
      p.dispose();
    };
  });
</script>

<header class="page-head">
  <div>
    <h1>法律事務所</h1>
  </div>
  <div class="search-actions">
    <form class="search-form" onsubmit={submitSearch}>
      <input
        type="search"
        class="search-box"
        bind:value={qInput}
        maxlength={MAX_LIST_VIEW_SEARCH_LENGTH}
        placeholder="事務所名・住所で検索"
        aria-label="事務所名・住所で検索"
      />
    </form>
    <KebabMenu label="その他の操作" bordered>
      <button
        type="button"
        class="menu-item firm-bulk-menu-item"
        role="menuitem"
        data-testid="firm-advanced-filter-entry"
        onclick={openAdvancedFilter}
      >
        <span>詳細条件で絞り込み</span>
        <small>詳細画面の項目を使って条件を追加します</small>
      </button>
      {#if allowBulkEdit}
        <button
          type="button"
          class="menu-item firm-bulk-menu-item"
          role="menuitem"
          data-testid="firm-bulk-query-entry"
          disabled={bulkMenuDisabled}
          onclick={openBulkAssigneeForQuery}
        >
          <span>現在の検索条件に一致する全件に担当者を割り当て</span>
          <small>ページングを除く検索結果全体を対象にします</small>
        </button>
        <button
          type="button"
          class="menu-item firm-bulk-menu-item"
          role="menuitem"
          data-testid="firm-bulk-select-rows-entry"
          disabled={selectionModeActive || bulkMenuDisabled}
          onclick={startRowSelectionMode}
        >
          <span>行を選択して担当者を割り当て</span>
          <small>チェックした法律事務所だけを対象にします</small>
        </button>
      {/if}
    </KebabMenu>
  </div>
</header>

{#if activeFilterChips.length > 0}
  <div class="filter-summary" aria-label="現在の検索条件">
    {#each activeFilterChips as chip (chip.key)}
      <button
        type="button"
        class="summary-chip"
        data-testid="firm-filter-chip"
        onclick={chip.onRemove}
      >
        <span>{chip.label}</span>
        <span aria-hidden="true">×</span>
      </button>
    {/each}
    <button type="button" class="summary-clear" onclick={clearFilters}
      >クリア</button
    >
  </div>
{/if}

<section>
  {#if firmListStore.loading && firmListStore.data === null}
    <div class="panel"><p class="muted">読み込み中…</p></div>
  {:else if firmListStore.error}
    <div class="error">{firmListStore.error}</div>
  {:else if firmListStore.data}
    <div class="panel">
      {#if bulkStatusMessage}
        <div class="bulk-status" role="status">{bulkStatusMessage}</div>
      {/if}
      {#if accountLoadError}
        <div class="bulk-warning">
          担当者候補を取得できませんでした: {accountLoadError}
        </div>
      {/if}
      {#if selectionModeActive}
        <div
          class="selection-bar"
          role="region"
          aria-label="行選択操作"
          data-testid="firm-selection-bar"
        >
          <span class="selection-count">{selectedCountLabel}件選択中</span>
          <div class="selection-actions">
            <button
              type="button"
              data-testid="firm-select-visible"
              disabled={selectVisibleRowsDisabled}
              onclick={selectVisibleRows}
            >
              表示中をすべて選択
            </button>
            <button
              type="button"
              class="primary"
              data-testid="firm-assign-selected"
              disabled={assignSelectedRowsDisabled}
              onclick={openBulkAssigneeForSelectedIds}
            >
              担当者を割り当て
            </button>
            <button
              type="button"
              data-testid="firm-selection-cancel"
              onclick={cancelSelectionMode}
            >
              キャンセル
            </button>
          </div>
        </div>
      {/if}
      <FirmTable
        firms={firmListStore.data.items}
        {query}
        productOptions={productFilterOptions}
        accountOptions={accountFilterOptions}
        {accountLabelOptions}
        canSelect={allowBulkEdit && selectionModeActive}
        selectionDisabled={bulkSelectionDisabled}
        {selectedIds}
        {hasActiveFilters}
        onChange={applyQuery}
        onSort={toggleSort}
        onClearFilters={clearFilters}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
      />
      <Pagination
        total={firmListStore.data.total}
        page={firmListStore.data.page}
        pageSize={firmListStore.data.page_size}
        onChange={changePage}
      />
    </div>
  {/if}
</section>

<FirmBulkAssigneeModal
  open={bulkModalOpen}
  initialSelectionMode={bulkInitialSelectionMode}
  allowedSelectionModes={bulkAllowedSelectionModes}
  selectedIds={selectedFirmIds}
  total={firmListStore.data?.total ?? 0}
  {query}
  {accountOptions}
  {accountLabelOptions}
  onClose={closeBulkAssignee}
  onDone={handleBulkDone}
/>

<FirmAdvancedFilterDrawer
  open={advancedFilterOpen}
  {query}
  accountOptions={accountFilterOptions}
  competitorServiceOptions={competitorServiceFilterOptions}
  total={firmListStore.data?.total ?? 0}
  onClose={closeAdvancedFilter}
  onApply={applyQuery}
  onClear={clearFilters}
/>

<style>
  /* src/pages/* の `.page-head` (タイトル + メタ) と同じレイアウト。
     検索ボックスを右上に配置する。 */
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .search-actions {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
  .search-form {
    display: flex;
  }
  .search-box {
    width: 360px;
    max-width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    font-size: 13px;
    background: #ffffff;
  }
  .search-box:focus {
    outline: none;
    border-color: #2563eb;
  }
  .filter-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
  }
  .summary-chip,
  .summary-clear {
    height: 30px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border: 1px solid #d7dee8;
    border-radius: 999px;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
  }
  .summary-chip:hover,
  .summary-clear:hover {
    background: #f9fafb;
  }
  .summary-chip span:last-child {
    color: #6b7280;
    font-weight: 700;
  }
  .summary-clear {
    color: #2563eb;
    border-color: #bfdbfe;
  }
  .bulk-status,
  .bulk-warning {
    margin-bottom: 12px;
  }
  .bulk-status {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 6px;
    color: #065f46;
    font-size: 13px;
    padding: 8px 12px;
  }
  .bulk-warning {
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    color: #92400e;
    font-size: 13px;
    padding: 8px 12px;
  }
  .selection-bar {
    align-items: center;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 10px 12px;
    flex-wrap: wrap;
  }
  .selection-count {
    color: #1e3a8a;
    font-size: 13px;
    font-weight: 700;
  }
  .selection-actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .selection-actions button {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    color: #1f2937;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    padding: 7px 10px;
  }
  .selection-actions button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
  }
  .selection-actions button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  :global(.firm-bulk-menu-item) {
    align-items: flex-start;
    display: flex !important;
    flex-direction: column;
    gap: 2px;
    min-width: 244px;
    white-space: normal !important;
  }
  :global(.firm-bulk-menu-item small) {
    color: #6b7280;
    font-size: 11px;
    line-height: 1.3;
  }
  @media (max-width: 640px) {
    .search-actions,
    .search-form {
      width: 100%;
    }
    .search-box {
      flex: 1 1 auto;
      width: 100%;
    }
    .selection-actions {
      width: 100%;
    }
    .selection-actions button {
      flex: 1 1 auto;
    }
  }
</style>
